import { CameraIcon, ShieldCheckIcon, ShieldOffIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Toggle } from '../ui/Toggle';
import { EmptyState } from '../ui/EmptyState';
import { CompareSlider } from '../../ui/CompareSlider';
import { diaryWeek } from '../../utils/account';
import { dayMedium } from '../../core/format/dates';
import type { CustomerFileControl } from './useCustomerFile';

/**
 * Het fotodagboek van één klant. Sarah kijkt mee om te adviseren; een foto
 * gebruiken voor marketing kan alleen met toestemming van de klant — en die
 * trekt ze zelf in Mijn omgeving weer in.
 */
export function CustomerDiary({
  ctl
}: {
  ctl: CustomerFileControl;
}) {
  const {
    account
  } = ctl.file;
  const {
    firstName,
    diaryConsent
  } = account.customer;
  const entries = [...account.diary].sort((a, b) => a.date.localeCompare(b.date));
  const first = entries[0];
  const last = entries[entries.length - 1];
  const consent = <div className={`mb-6 flex items-start gap-3 rounded-lg px-4 py-3.5 text-[14px] text-ink ${diaryConsent ? 'bg-sage/15' : 'bg-sand'}`} role="note" data-id="element-1177" data-mp="2v6ucb8o8yekt">
      {diaryConsent ? <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={1.75} aria-hidden="true" data-id="element-1178" data-mp="2m2c78htjlkxb" /> : <ShieldOffIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-1179" data-mp="2819ks3ymy8lv" />}
      <p className="leading-relaxed" data-id="element-1180" data-mp="2czb3uow2a7xc">
        {diaryConsent ? `${firstName} vindt het goed dat je haar foto’s geanonimiseerd gebruikt. Markeer de foto’s die je wilt gebruiken; alleen die komen in aanmerking.` : `${firstName} heeft geen toestemming gegeven voor gebruik van haar foto’s. Je ziet ze alleen om mee te kijken en te adviseren.`}
      </p>
    </div>;
  if (!first || !last) {
    return <>
        {consent}
        <EmptyState icon={CameraIcon} title="Nog geen foto’s" text={`${firstName} kan in Mijn omgeving foto’s maken. Vraag het gerust na een behandeling: dan ziet ze straks zelf het verschil.`} data-id="element-1181" data-mp="1fcua1tmjecgc" />
      </>;
  }
  const canCompare = first.id !== last.id;
  return <div data-id="element-1182" data-mp="3hanz1l4ibghx">
      {consent}
      <div className="grid gap-6 lg:grid-cols-5" data-id="element-1183" data-mp="3ha4j29yf326j">
        {canCompare && <Panel title="Voor en na" description={`Week 1 tegenover week ${diaryWeek(first.date, last.date)}.`} className="lg:col-span-2" data-id="element-1184" data-mp="3d8jah0sx1a25">
            <CompareSlider before={{
          image: first.image,
          label: dayMedium(first.date)
        }} after={{
          image: last.image,
          label: dayMedium(last.date)
        }} data-id="element-1185" data-mp="38c2j7xqf6fku" />
          </Panel>}
        <ul className={`grid grid-cols-2 gap-4 sm:grid-cols-3 ${canCompare ? 'lg:col-span-3' : 'lg:col-span-5'}`} data-id="element-1186" data-mp="4uu3bjf95psr">
          {entries.map(d => <li key={d.id} className="overflow-hidden rounded-lg border border-line bg-white" data-id="element-1187" data-mp="3r0pmqbsy6wwb">
              <img src={d.image} alt={`Foto van ${firstName}, ${dayMedium(d.date)}`} className="aspect-[4/5] w-full object-cover" data-id="element-1188" data-mp="2kkncztvive4b" />
              <div className="p-3" data-id="element-1189" data-mp="2flaq594ni71q">
                <p className="text-[13px] text-ink" data-id="element-1190" data-mp="2fk92m56g7s34">
                  Week {diaryWeek(first.date, d.date)} <span className="text-muted" data-id="element-1191" data-mp="25j6ukzkejce5">· {dayMedium(d.date)}</span>
                </p>
                {d.note && <p className="mt-1 line-clamp-3 text-[12px] leading-snug text-muted" data-id="element-1192" data-mp="2agzxnjqerqa7">“{d.note}”</p>}
                {diaryConsent && <div className="mt-2 border-t border-line pt-2" data-id="element-1193" data-mp="2k6jjmy9f409a">
                    <Toggle checked={Boolean(d.marketing)} onChange={v => ctl.markDiary(d.id, v)} label="Voor marketing" data-id="element-1194" data-mp="2k7uney2v580e" />
                  </div>}
              </div>
            </li>)}
        </ul>
      </div>
    </div>;
}